import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const TABLE_NAME = 'rd_cco';
const COLATE = ['CCA', 'CCB'];

const normalizeText = value =>
  String(value ?? '')
    .trim()
    .toUpperCase();

const normalizeLine = value =>
  String(value ?? '').trim();

const validDate = value => {
  const time = new Date(value).getTime();

  return Number.isFinite(time)
    ? time
    : null;
};

const formatDate = value => {
  const time = validDate(value);

  if (time === null) {
    return 'N/D';
  }

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: 'Europe/Rome',
  }).format(new Date(time));
};

const formatDateTime = value => {
  const time = validDate(value);

  if (time === null) {
    return 'N/D';
  }

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Rome',
  }).format(new Date(time));
};

const daysSince = value => {
  const time = validDate(value);

  if (time === null) {
    return null;
  }

  return Math.max(
    0,
    Math.floor(
      (Date.now() - time) / 86400000
    )
  );
};

const sortNatural = values =>
  [...values].sort((a, b) =>
    String(a).localeCompare(
      String(b),
      'it',
      {
        numeric: true,
        sensitivity: 'base',
      }
    )
  );

const colataTheme = colata =>
  normalizeText(colata) === 'CCA'
    ? {
        text: 'text-blue-500',
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        active: 'bg-blue-500 text-white',
        dot: 'bg-blue-500',
        rgb: [59, 130, 246],
      }
    : {
        text: 'text-violet-500',
        bg: 'bg-violet-500/10',
        border: 'border-violet-500/30',
        active: 'bg-violet-500 text-white',
        dot: 'bg-violet-500',
        rgb: [139, 92, 246],
      };

/*
 * Ordine delle priorità:
 *
 * 1. Postazione libera -> grigio
 * 2. Macchina più recente -> verde, SEMPRE
 * 3. Macchina più vecchia -> rosso
 * 4. Oltre 2 anni -> arancio
 * 5. Oltre un anno -> ambra
 * 6. Tutte le altre -> grigio neutro
 *
 * "Recente" qui NON dipende dai giorni:
 * è la macchina con il data_intervento più recente
 * nella colata visualizzata.
 */
const ageTheme = ({
  days,
  oldest,
  newest,
  active,
}) => {
  if (!active) {
    return {
      label: 'Postazione libera',
      text: 'text-slate-400',
      bg: 'bg-muted/5',
      border: 'border-dashed border-border/20',
      dot: 'bg-slate-300',
      icon: 'CircleDashed',
    };
  }

  if (newest) {
    return {
      label: 'Recente',
      text: 'text-emerald-600 dark:text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/45',
      dot: 'bg-emerald-500 animate-pulse shadow-[0_0_10px_rgba(16,185,129,.65)]',
      icon: 'CheckCircle2',
    };
  }

  if (oldest) {
    return {
      label: 'Più vecchia',
      text: 'text-red-500',
      bg: 'bg-red-500/10',
      border: 'border-red-500/45',
      dot: 'bg-red-500 animate-pulse shadow-[0_0_10px_rgba(239,68,68,.7)]',
      icon: 'AlertTriangle',
    };
  }

  if (days >= 730) {
    return {
      label: 'Oltre 2 anni',
      text: 'text-orange-500',
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/35',
      dot: 'bg-orange-500',
      icon: 'Clock3',
    };
  }

  if (days >= 365) {
    return {
      label: 'Oltre un anno',
      text: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/30',
      dot: 'bg-amber-500',
      icon: 'Clock',
    };
  }

  return {
    label: 'In uso',
    text: 'text-slate-500 dark:text-slate-300',
    bg: 'bg-slate-500/10',
    border: 'border-slate-500/25',
    dot: 'bg-slate-400',
    icon: 'Activity',
  };
};

const MachineSlot = ({
  machine,
  isOldest,
  isNewest,
  onOpen,
}) => {
  const active = Boolean(machine?.n_macchina);

  const days = active
    ? daysSince(machine.data_intervento)
    : null;

  const theme = ageTheme({
    days,
    oldest: isOldest,
    newest: isNewest,
    active,
  });

  return (
    <button
      type="button"
      disabled={!active}
      onClick={() => active && onOpen(machine)}
      className={`w-full text-left flex flex-col gap-3 p-4 rounded-2xl border transition-all duration-200 ${
        active
          ? `${theme.bg} ${theme.border} hover:scale-[1.015] hover:shadow-lg cursor-pointer`
          : `${theme.bg} ${theme.border} opacity-70 cursor-default`
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
              active
                ? `${theme.bg} ${theme.text}`
                : 'bg-muted/20 text-muted-foreground/30'
            }`}
          >
            <Icon
              name={active ? 'Settings2' : theme.icon}
              size={13}
            />
          </div>

          <div className="min-w-0">
            <span className="block text-[9px] font-black uppercase tracking-widest text-muted-foreground/45">
              Macchina
            </span>

            <span
              className={`block text-[9px] font-black uppercase tracking-widest truncate ${theme.text}`}
            >
              {theme.label}
            </span>
          </div>
        </div>

        {active && (
          <span
            className={`w-2 h-2 rounded-full shrink-0 ${theme.dot}`}
          />
        )}
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <span
            className={`block text-sm sm:text-base font-black uppercase tracking-tight truncate ${
              active
                ? 'text-foreground'
                : 'text-muted-foreground/25'
            }`}
          >
            {active
              ? machine.n_macchina
              : '—'}
          </span>

          <span className="block mt-1 text-[9px] font-black uppercase tracking-widest text-muted-foreground/40 truncate">
            {active
              ? machine.linea
              : 'Nessuna macchina attiva'}
          </span>
        </div>

        {active && (
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span
              className={`flex items-center gap-1 text-[10px] font-black whitespace-nowrap ${theme.text}`}
            >
              <Icon
                name="CalendarDays"
                size={11}
              />

              {days === 0
                ? 'Oggi'
                : `${days} gg`}
            </span>

            <span className="text-[9px] font-bold tabular-nums text-muted-foreground/45 whitespace-nowrap">
              {formatDate(
                machine.data_intervento
              )}
            </span>
          </div>
        )}
      </div>

      {active && (
        <div className="pt-2 border-t border-border/20 flex items-center justify-center">
          <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/45">
            Apri dettagli
          </span>
        </div>
      )}
    </button>
  );
};

const LineaCard = ({
  linea,
  colata,
  machine,
  isOldest,
  isNewest,
  onOpen,
}) => {
  const theme = colataTheme(colata);

  const active = Boolean(
    machine?.n_macchina
  );

  return (
    <div
      className={`relative flex flex-col p-5 rounded-[2rem] border transition-all duration-200 ${
        active
          ? 'bg-card border-border/40 hover:border-primary/20 hover:shadow-xl'
          : 'bg-muted/5 border-dashed border-border/20'
      }`}
    >
      {isNewest && active && (
        <div className="absolute -top-2.5 left-5 px-3 py-1 rounded-full bg-emerald-500 text-white text-[8px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/30">
          Recente
        </div>
      )}

      {isOldest && active && (
        <div className="absolute -top-2.5 right-5 px-3 py-1 rounded-full bg-red-500 text-white text-[8px] font-black uppercase tracking-widest shadow-lg shadow-red-500/30">
          Più vecchia
        </div>
      )}

      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <span
            className={`block text-[9px] font-black uppercase tracking-[.3em] mb-1 ${theme.text}`}
          >
            {colata}
          </span>

          <span className="text-4xl font-black uppercase tracking-tighter text-foreground leading-none">
            {linea}
          </span>
        </div>

        <div
          className={`w-10 h-10 rounded-2xl flex items-center justify-center border shrink-0 ${
            active
              ? `${theme.bg} ${theme.border} ${theme.text}`
              : 'bg-muted/20 border-border/20 text-muted-foreground/20'
          }`}
        >
          <Icon
            name="GitBranch"
            size={17}
          />
        </div>
      </div>

      <MachineSlot
        machine={machine}
        isOldest={isOldest}
        isNewest={isNewest}
        onOpen={onOpen}
      />
    </div>
  );
};

const SkeletonCard = () => (
  <div className="flex flex-col gap-4 p-5 rounded-[2rem] bg-muted/10 border border-border/20 animate-pulse">
    <div className="h-2 w-12 rounded bg-muted/40" />
    <div className="h-10 w-28 rounded-lg bg-muted/40" />
    <div className="h-36 rounded-2xl bg-muted/30" />
  </div>
);

const DetailModal = ({
  item,
  onClose,
}) => {
  if (!item) {
    return null;
  }

  const days = daysSince(
    item.data_intervento
  );

  const theme = ageTheme({
    days,
    oldest: item.isOldest,
    newest: item.isNewest,
    active: true,
  });

  return (
    <div className="fixed inset-0 z-[450] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Chiudi dettagli"
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-[2rem] bg-card border border-border/60 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        <div
          className={`h-1 ${
            item.isNewest
              ? 'bg-emerald-500'
              : item.isOldest
                ? 'bg-red-500'
                : 'bg-primary'
          }`}
        />

        <div className="p-6">
          <div className="flex justify-between gap-4">
            <div className="min-w-0">
              <span className="text-[10px] font-black uppercase tracking-[.25em] text-primary/60">
                Dettaglio montaggio
              </span>

              <h3 className="mt-1 text-xl sm:text-2xl font-black uppercase tracking-tight truncate">
                {item.n_macchina}
              </h3>

              <span className="text-[11px] font-bold text-muted-foreground/50">
               {item.linea} · {item.colata}
              </span>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-10 h-10 shrink-0 rounded-2xl bg-muted/50 border border-border/40 flex items-center justify-center"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          <div
            className={`mt-6 p-4 rounded-2xl border ${theme.bg} ${theme.border}`}
          >
            <div className="flex justify-between gap-3">
              <span
                className={`flex items-center gap-2 text-[11px] font-black uppercase tracking-widest ${theme.text}`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${theme.dot}`}
                />

                {theme.label}
              </span>

              <span
                className={`text-lg font-black ${theme.text}`}
              >
                {days === 0
                  ? 'Oggi'
                  : `${days} giorni`}
              </span>
            </div>

            <span className="block mt-2 text-[10px] font-bold text-muted-foreground/55">
              Montata il{' '}
              {formatDateTime(
                item.data_intervento
              )}
            </span>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            {[
              ['Linea', item.linea],
              ['Colata', item.colata],
              ['Tipo', item.tipointervento],
              ['Stato', item.stato],
            ].map(([label, value]) => (
              <div
                key={label}
                className="p-3 rounded-xl bg-muted/20 border border-border/25"
              >
                <span className="block text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">
                  {label}
                </span>

                <span className="block mt-1 text-[12px] font-black text-foreground/75 truncate">
                  {value || '---'}
                </span>
              </div>
            ))}
          </div>

          {item.note && (
            <div className="mt-4 p-4 rounded-2xl bg-muted/20 border border-border/25">
              <span className="block text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">
                Note
              </span>

              <p className="mt-1 text-xs font-bold text-foreground/70">
                {item.note}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const LineeRaddrizzatriciOverviewModal = ({
  isOpen,
  onClose,
}) => {
  const [
    selectedColata,
    setSelectedColata,
  ] = useState('CCA');

  const [
    machinesByLine,
    setMachinesByLine,
  ] = useState({});

  const [
    lineeKeys,
    setLineeKeys,
  ] = useState([]);

  const [
    isLoading,
    setIsLoading,
  ] = useState(false);

  const [
    isExporting,
    setIsExporting,
  ] = useState(false);

  const [
    errorMessage,
    setErrorMessage,
  ] = useState('');

  const [
    selectedMachine,
    setSelectedMachine,
  ] = useState(null);

  const fetchData = useCallback(async colata => {
    setIsLoading(true);
    setErrorMessage('');

    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(`
          id,
          n_macchina,
          linea,
          colata,
          data_intervento,
          data_rientro,
          tipointervento,
          stato,
          posizione,
          stato_rid,
          causa_freno,
          note
        `)
        .eq('tipointervento', 'MONTATO')
        .eq('colata', colata)
        .not('n_macchina', 'is', null)
        .not('linea', 'is', null)
        .not('data_intervento', 'is', null)
        .order('data_intervento', {
          ascending: false,
        });

      if (error) {
        throw error;
      }

      const machines = {};
      const linee = new Set();

      for (const row of data || []) {
        const linea = normalizeLine(
          row.linea
        );

        const macchina = normalizeLine(
          row.n_macchina
        );

        if (!linea || !macchina) {
          continue;
        }

        linee.add(linea);

        /*
         * Query ordinata DESC:
         * il primo record trovato per la linea
         * è il MONTATO più recente.
         */
        if (!machines[linea]) {
          machines[linea] = {
            ...row,
            linea,
            n_macchina: macchina,
          };
        }
      }

      setLineeKeys(
        sortNatural([...linee])
      );

      setMachinesByLine(machines);
    } catch (error) {
      console.error(
        'Errore caricamento MONTATO rd_cco:',
        error
      );

      setMachinesByLine({});
      setLineeKeys([]);

      setErrorMessage(
        error?.message ||
          'Errore durante il caricamento delle macchine montate.'
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData(selectedColata);
    }
  }, [
    isOpen,
    selectedColata,
    fetchData,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handler = event => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    window.addEventListener(
      'keydown',
      handler
    );

    const previous =
      document.body.style.overflow;

    document.body.style.overflow =
      'hidden';

    return () => {
      window.removeEventListener(
        'keydown',
        handler
      );

      document.body.style.overflow =
        previous;
    };
  }, [
    isOpen,
    onClose,
  ]);

  const activeMachines = useMemo(
    () => Object.values(machinesByLine),
    [machinesByLine]
  );

  const oldestMachineId = useMemo(() => {
    if (!activeMachines.length) {
      return null;
    }

    return activeMachines.reduce(
      (oldest, current) =>
        validDate(
          current.data_intervento
        ) <
        validDate(
          oldest.data_intervento
        )
          ? current
          : oldest
    ).id;
  }, [activeMachines]);

  /*
   * Macchina più giovane:
   * è quella con la data MONTATO più recente.
   * Verrà marcata verde e "Recente"
   * indipendentemente dal numero di giorni.
   */
  const newestMachineId = useMemo(() => {
    if (!activeMachines.length) {
      return null;
    }

    return activeMachines.reduce(
      (newest, current) =>
        validDate(
          current.data_intervento
        ) >
        validDate(
          newest.data_intervento
        )
          ? current
          : newest
    ).id;
  }, [activeMachines]);

  const getRowsForColata = async colata => {
    const { data, error } = await supabase
      .from(TABLE_NAME)
      .select(`
        id,
        n_macchina,
        linea,
        colata,
        data_intervento,
        data_rientro,
        tipointervento,
        stato,
        posizione,
        stato_rid,
        causa_freno,
        note
      `)
      .eq('tipointervento', 'MONTATO')
      .eq('colata', colata)
      .not('n_macchina', 'is', null)
      .not('linea', 'is', null)
      .not('data_intervento', 'is', null)
      .order('data_intervento', {
        ascending: false,
      });

    if (error) {
      throw error;
    }

    const latest = {};

    for (const row of data || []) {
      const linea = normalizeLine(
        row.linea
      );

      const macchina = normalizeLine(
        row.n_macchina
      );

      if (!linea || !macchina) {
        continue;
      }

      if (!latest[linea]) {
        latest[linea] = {
          ...row,
          linea,
          n_macchina: macchina,
          isMounted: true,
        };
      }
    }

    return sortNatural(
      Object.keys(latest)
    ).map(linea => latest[linea]);
  };

  const generatePDF = async () => {
    setIsExporting(true);

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      const now = new Date().toLocaleString(
        'it-IT'
      );

      const pages = await Promise.all(
        COLATE.map(getRowsForColata)
      );

      pages.forEach((rows, pageIndex) => {
        if (pageIndex) {
          doc.addPage();
        }

        const colata = COLATE[pageIndex];
        const theme = colataTheme(colata);

        const oldest = rows.length
          ? rows.reduce((a, b) =>
              validDate(
                a.data_intervento
              ) <=
              validDate(
                b.data_intervento
              )
                ? a
                : b
            ).id
          : null;

        const newest = rows.length
          ? rows.reduce((a, b) =>
              validDate(
                a.data_intervento
              ) >=
              validDate(
                b.data_intervento
              )
                ? a
                : b
            ).id
          : null;

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);

        doc.text(
          'OrizonDesk — Monitoraggio Raddrizzatrici',
          14,
          12
        );

        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);

        doc.text(
          `MONITORAGGIO LINEE — ${colata}`,
          14,
          24
        );

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);

        doc.text(
          `Stato attuale al: ${now}`,
          14,
          31
        );

        doc.setDrawColor(...theme.rgb);
        doc.setLineWidth(0.6);
        doc.line(14, 35, 283, 35);

        autoTable(doc, {
          startY: 43,
          head: [[
            'LINEA',
            'N° MACCHINA',
            'DATA MONTAGGIO',
            'GIORNI',
            'TIPO',
            'STATO',
            'SEGNALAZIONE',
          ]],
          body: rows.map(row => {
            const days = daysSince(
              row.data_intervento
            );

            const flag =
              row.id === newest
                ? 'RECENTE'
                : row.id === oldest
                  ? 'PIÙ VECCHIA'
                  : days >= 730
                    ? 'OLTRE 2 ANNI'
                    : days >= 365
                      ? 'OLTRE UN ANNO'
                      : 'IN USO';

            return [
              `${row.linea}`,
              row.n_macchina,
              formatDate(
                row.data_intervento
              ),
              days === 0
                ? 'Oggi'
                : `${days} gg`,
              row.tipointervento || '---',
              row.stato || '---',
              flag,
            ];
          }),
          theme: 'striped',
          margin: {
            left: 14,
            right: 14,
            bottom: 20,
          },
          headStyles: {
            fillColor: theme.rgb,
            textColor: 255,
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'center',
          },
          bodyStyles: {
            fontSize: 8,
            cellPadding: 3.5,
          },
          columnStyles: {
            0: {
              fontStyle: 'bold',
            },
            1: {
              fontStyle: 'bold',
              halign: 'center',
            },
            2: {
              halign: 'center',
            },
            3: {
              halign: 'center',
            },
            4: {
              halign: 'center',
            },
            5: {
              halign: 'center',
            },
            6: {
              halign: 'center',
              fontStyle: 'bold',
            },
          },
          didParseCell: cell => {
            if (
              cell.section !== 'body' ||
              cell.column.index !== 6
            ) {
              return;
            }

            const row =
              rows[cell.row.index];

            if (!row) {
              return;
            }

            /*
             * La macchina più recente è sempre verde:
             * priorità maggiore rispetto ai giorni.
             */
            if (row.id === newest) {
              cell.cell.styles.textColor = [
                22,
                163,
                74,
              ];

              cell.cell.styles.fontStyle =
                'bold';

              return;
            }

            if (row.id === oldest) {
              cell.cell.styles.textColor = [
                220,
                38,
                38,
              ];

              cell.cell.styles.fontStyle =
                'bold';

              return;
            }

            const days = daysSince(
              row.data_intervento
            );

            if (days >= 730) {
              cell.cell.styles.textColor = [
                249,
                115,
                22,
              ];
            } else if (days >= 365) {
              cell.cell.styles.textColor = [
                217,
                119,
                6,
              ];
            } else {
              cell.cell.styles.textColor = [
                100,
                116,
                139,
              ];
            }
          },
          didDrawPage: () => {
            const page = doc.internal.pageSize;

            const width =
              page.width ||
              page.getWidth();

            const height =
              page.height ||
              page.getHeight();

            doc.setDrawColor(230);

            doc.line(
              14,
              height - 15,
              width - 14,
              height - 15
            );

            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.setFont(
              'helvetica',
              'normal'
            );

            doc.text(
              `Stampato il: ${now}`,
              14,
              height - 10
            );

            doc.text(
              `OrizonDesk — Pagina ${doc.internal.getNumberOfPages()}`,
              width / 2,
              height - 10,
              {
                align: 'center',
              }
            );

            doc.text(
              'https://desk.orizon-aix.com',
              width - 14,
              height - 10,
              {
                align: 'right',
              }
            );
          },
        });
      });

      doc.save(
        `Monitoraggio_Raddrizzatrici_${new Date()
          .toISOString()
          .slice(0, 10)}.pdf`
      );
    } catch (error) {
      console.error(
        'Errore PDF:',
        error
      );

      alert(
        'Errore durante la generazione del PDF.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const theme = colataTheme(
    selectedColata
  );

  const totalMounted =
    activeMachines.length;

  return (
    <>
      <div className="fixed inset-0 z-[400] flex items-center justify-center p-2 sm:p-4">
        <button
          type="button"
          aria-label="Chiudi monitoraggio"
          onClick={onClose}
          className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300"
        />

        <div className="relative w-full max-w-6xl bg-card border border-border shadow-2xl rounded-[2rem] sm:rounded-[3rem] overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[90vh] animate-in zoom-in-95 duration-300">
          <div className="shrink-0 px-5 sm:px-8 py-4 sm:py-6 border-b border-border/40 bg-card/80 backdrop-blur-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
                  <Icon
                    name="Activity"
                    size={19}
                  />
                </div>

                <div className="min-w-0">
                  <h2 className="text-base sm:text-xl font-black uppercase tracking-tighter truncate">
                    Monitoraggio Linee
                  </h2>

                  <p className="text-[8px] sm:text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/40 mt-0.5 truncate">
                    Raddrizzatrici montate da rd_cco
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 ml-auto sm:ml-0">
                <div className="flex p-1 rounded-xl bg-muted/60 border border-border/50">
                  {COLATE.map(colata => {
                    const current =
                      selectedColata === colata;

                    const buttonTheme =
                      colataTheme(colata);

                    return (
                      <button
                        type="button"
                        key={colata}
                        onClick={() =>
                          setSelectedColata(
                            colata
                          )
                        }
                        className={`px-4 sm:px-6 py-2 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all ${
                          current
                            ? `${buttonTheme.active} shadow-lg`
                            : 'text-muted-foreground/60 hover:text-foreground'
                        }`}
                      >
                        {colata}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={generatePDF}
                  disabled={isExporting}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-500 hover:bg-blue-500 hover:text-white disabled:opacity-40 shrink-0"
                  title="Genera PDF"
                >
                  <Icon
                    name={
                      isExporting
                        ? 'Loader2'
                        : 'FileDown'
                    }
                    size={14}
                    className={
                      isExporting
                        ? 'animate-spin'
                        : ''
                    }
                  />
                </button>

                <button
                  type="button"
                  onClick={() =>
                    fetchData(
                      selectedColata
                    )
                  }
                  disabled={isLoading}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-muted/40 border border-border/30 text-muted-foreground hover:text-primary disabled:opacity-40 shrink-0"
                  title="Aggiorna"
                >
                  <Icon
                    name="RefreshCw"
                    size={14}
                    className={
                      isLoading
                        ? 'animate-spin'
                        : ''
                    }
                  />
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-500/5 border border-red-500/10 text-red-500/70 hover:text-red-500 shrink-0"
                >
                  <Icon
                    name="X"
                    size={16}
                  />
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <div
                className={`px-5 py-2.5 rounded-2xl ${theme.bg} border ${theme.border} flex items-center gap-2`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${theme.dot}`}
                />

                <span
                  className={`text-base font-black uppercase tracking-widest ${theme.text}`}
                >
                  {selectedColata}
                </span>
              </div>

              <div className="px-4 py-2.5 rounded-2xl bg-muted/40 border border-border/30 flex items-center gap-1.5">
                <span className="text-[11px] font-black text-foreground">
                  {totalMounted}
                </span>

                <span className="text-[9px] text-muted-foreground/30">
                  /
                </span>

                <span className="text-[11px] font-black text-muted-foreground/50">
                  {lineeKeys.length}
                </span>

                <span className="ml-1 text-[9px] font-black uppercase tracking-widest text-muted-foreground/35">
                  montate
                </span>
              </div>

              <div className="w-full sm:w-auto sm:flex-1 flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2 sm:gap-4">
                <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                  <i className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  Recente
                </span>

                <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">
                  <i className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                  Oltre un anno
                </span>

                <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-orange-500">
                  <i className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                  Oltre 2 anni
                </span>

                <span className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-red-500">
                  <i className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                  Più vecchia
                </span>
              </div>
            </div>
          </div>

          <div
            className="flex-1 overflow-y-auto p-4 sm:p-8"
            style={{
              scrollbarWidth: 'thin',
            }}
          >
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[1, 2, 3, 4].map(item => (
                  <SkeletonCard key={item} />
                ))}
              </div>
            ) : errorMessage ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <Icon
                  name="TriangleAlert"
                  size={38}
                  className="text-red-500/60"
                />

                <span className="max-w-md text-center text-sm font-bold text-red-500/70">
                  {errorMessage}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    fetchData(
                      selectedColata
                    )
                  }
                  className="px-5 py-2.5 rounded-xl bg-primary/10 border border-primary/20 text-primary text-[10px] font-black uppercase tracking-widest"
                >
                  Riprova
                </button>
              </div>
            ) : !lineeKeys.length ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Icon
                  name="Inbox"
                  size={38}
                  className="text-muted-foreground/20"
                />

                <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/30">
                  Nessuna linea trovata per{' '}
                  {selectedColata}
                </span>
              </div>
            ) : (
              <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {lineeKeys.map(linea => (
                  <LineaCard
                    key={linea}
                    linea={linea}
                    colata={selectedColata}
                    machine={
                      machinesByLine[linea]
                    }
                    isOldest={
                      machinesByLine[linea]
                        ?.id === oldestMachineId
                    }
                    isNewest={
                      machinesByLine[linea]
                        ?.id === newestMachineId
                    }
                    onOpen={item =>
                      setSelectedMachine({
                        ...item,
                        isOldest:
                          item.id ===
                          oldestMachineId,
                        isNewest:
                          item.id ===
                          newestMachineId,
                      })
                    }
                  />
                ))}
              </div>
            )}
          </div>

          <div className="shrink-0 px-5 sm:px-8 py-4 border-t border-border/40 bg-muted/10 flex items-center justify-between gap-3">
            <span className="text-[9px] font-black uppercase tracking-[.2em] text-muted-foreground/40 truncate">
              {isLoading
                ? 'Aggiornamento...'
                : `Ultimo stato rilevato — ${selectedColata}`}
            </span>

            <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/35 whitespace-nowrap">
              {totalMounted} attive
            </span>
          </div>
        </div>
      </div>

      <DetailModal
        item={selectedMachine}
        onClose={() =>
          setSelectedMachine(null)
        }
      />
    </>
  );
};

export default LineeRaddrizzatriciOverviewModal;